import WeeklyAvailabilityBadge from './planner/WeeklyAvailabilityBadge';
import { buildSmartMatch } from "../services/talent/SmartMatchEngine";

function getAssignedUid(shift) {
  return String(
    shift?.assignedWorkerUid ||
      shift?.workerUid ||
      shift?.workerId ||
      (Array.isArray(shift?.assignedWorkerUids)
        ? shift.assignedWorkerUids[0]
        : "") ||
      ""
  );
}

function getWorkerUid(worker) {
  return String(worker?.uid || worker?.id || "");
}

function toDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (
    typeof value === "object" &&
    Number.isFinite(value.seconds)
  ) {
    return new Date(
      value.seconds * 1000 +
        Math.floor((value.nanoseconds || 0) / 1000000)
    );
  }

  const date = new Date(value);

  return Number.isFinite(date.getTime()) ? date : null;
}

function getShiftStart(shift) {
  return toDate(
    shift?.startTime ||
      shift?.startAt ||
      shift?.startDateTime ||
      shift?.shiftStart ||
      shift?.start
  );
}

function getShiftEnd(shift) {
  return toDate(
    shift?.endTime ||
      shift?.endAt ||
      shift?.endDateTime ||
      shift?.shiftEnd ||
      shift?.end
  );
}

function isClosedShift(shift) {
  const status = String(
    shift?.status || shift?.lifecycleStatus || ""
  ).toLowerCase();

  return [
    "completed",
    "cancelled",
    "canceled",
    "no_show",
    "no-show",
    "noshow",
    "deleted",
    "archived",
  ].includes(status);
}

function shiftsOverlap(firstShift, secondShift) {
  const firstStart = getShiftStart(firstShift);
  const firstEnd = getShiftEnd(firstShift);
  const secondStart = getShiftStart(secondShift);
  const secondEnd = getShiftEnd(secondShift);

  if (
    !firstStart ||
    !firstEnd ||
    !secondStart ||
    !secondEnd
  ) {
    return false;
  }

  return (
    firstStart.getTime() < secondEnd.getTime() &&
    secondStart.getTime() < firstEnd.getTime()
  );
}

function workerHasOverlappingShift(
  worker,
  selectedShift,
  shifts
) {
  const workerUid = getWorkerUid(worker);

  if (!workerUid) return true;

  return (Array.isArray(shifts) ? shifts : []).some(
    (shift) => {
      if (!shift) return false;

      if (
        String(shift.id || shift.shiftId || "") ===
        String(
          selectedShift?.id ||
            selectedShift?.shiftId ||
            ""
        )
      ) {
        return false;
      }

      if (isClosedShift(shift)) return false;

      const assignedWorkerUid = getAssignedUid(shift);

      const assignedWorkerUids = [
        ...(Array.isArray(shift?.assignedWorkerUids)
          ? shift.assignedWorkerUids
          : []),
        ...(Array.isArray(shift?.workerUids)
          ? shift.workerUids
          : []),
      ].map(String);

      const isAssignedToWorker =
        assignedWorkerUid === workerUid ||
        assignedWorkerUids.includes(workerUid);

      return (
        isAssignedToWorker &&
        shiftsOverlap(shift, selectedShift)
      );
    }
  );
}

function workerName(worker) {
  const fullName =
    worker?.displayName ||
    worker?.name ||
    `${worker?.firstName || ""} ${
      worker?.lastName || ""
    }`.trim();

  if (fullName) return fullName;

  const email = String(worker?.email || "").trim();

  if (email) {
    const friendlyName = email
      .split("@")[0]
      .replace(/[._-]+/g, " ")
      .replace(/\b\w/g, (letter) =>
        letter.toUpperCase()
      )
      .trim();

    if (friendlyName) return friendlyName;
  }

  return worker?.uid || worker?.id || "Worker";
}

function workerHasRequiredRole(worker, shiftRole) {
  if (!shiftRole) return false;

  const validTypes = Array.isArray(
    worker?._validTypes
  )
    ? worker._validTypes.map((value) =>
        String(value).toUpperCase()
      )
    : [];

  const approvedPositions = Array.isArray(
    worker?.approvedPositions
  )
    ? worker.approvedPositions.map((value) =>
        String(value).toUpperCase()
      )
    : [];

  const workerRoles = Array.isArray(worker?.roles)
    ? worker.roles.map((value) =>
        String(value).toUpperCase()
      )
    : [];

  const credentialRole = String(
    worker?.primaryRole ||
      worker?.workerType ||
      worker?.roleType ||
      worker?.licenseType ||
      ""
  ).toUpperCase();

  return (
    validTypes.includes(shiftRole) ||
    approvedPositions.includes(shiftRole) ||
    workerRoles.includes(shiftRole) ||
    credentialRole === shiftRole
  );
}

// Reuse Planner eligibility and its canonical facility roster without new reads.
export function passesRecommendationHardGuards(worker, shift, { tenantId, facilityId, roster, results }) {
  const normalize = value => String(value || "").trim().toLowerCase();
  const uid = getWorkerUid(worker);
  const result = results.find(row => row.workerUid === uid);
  return Boolean(uid && normalize(tenantId) && normalize(facilityId)) &&
    normalize(shift?.tenantId) === normalize(tenantId) &&
    normalize(shift?.facilityId) === normalize(facilityId) &&
    normalize(worker.tenantId) === normalize(tenantId) &&
    roster.some(row => getWorkerUid(row) === uid) &&
    result?.checks?.role === true && result?.checks?.credential === true &&
    result?.checks?.overlap === true;
}

export default function SelectedShiftAssistant({
  selectedShift,
  facility,
  workers = [],
  shifts = [],
  onAssign,
  passesHardGuards = () => false,
}) {
  if (!selectedShift?.id) return null;

  const alreadyAssigned = Boolean(
    getAssignedUid(selectedShift)
  );

  const shiftRole = String(
    selectedShift.role ||
      selectedShift.requiredPosition ||
      ""
  ).toUpperCase();

  const ranked = (Array.isArray(workers) ? workers : [])
    .filter((worker) => {
      const status = String(
        worker?.status || ""
      ).toLowerCase();

      const workerStatus = String(
        worker?.workerStatus || ""
      ).toLowerCase();

      const operational = String(
        worker?.workerOperationalState || ""
      ).toUpperCase();

      const active =
        status === "active" ||
        worker?.active === true;

      const ready =
        workerStatus === "ready" ||
        workerStatus === "ready_for_shifts" ||
        operational === "READY" ||
        operational === "READY_FOR_SHIFTS";

      const hasRequiredRole =
        workerHasRequiredRole(worker, shiftRole);

      const hasOverlap = workerHasOverlappingShift(
        worker,
        selectedShift,
        shifts
      );

      return (
        passesHardGuards(worker) === true &&
        active &&
        ready &&
        hasRequiredRole &&
        !hasOverlap
      );
    })
    .map((worker) => {
      const smart = buildSmartMatch(
        worker,
        selectedShift,
        { availabilityRanking: true, facility }
      );

      return {
        ...worker,
        _matchScore: Number(smart?.score || 0),
        _matchReasons: Array.isArray(smart?.reasons)
          ? smart.reasons
          : [],
      };
    })
    .sort((first, second) => {
      const scoreDifference =
        second._matchScore - first._matchScore;

      if (scoreDifference !== 0) {
        return scoreDifference;
      }

      const firstReliability = Number(
        first.reliabilityScore ||
          first.reliability ||
          0
      );

      const secondReliability = Number(
        second.reliabilityScore ||
          second.reliability ||
          0
      );

      if (
        secondReliability !== firstReliability
      ) {
        return secondReliability - firstReliability;
      }

      const firstAttendance = Number(
        first.attendanceScore ||
          first.attendance ||
          0
      );

      const secondAttendance = Number(
        second.attendanceScore ||
          second.attendance ||
          0
      );

      if (secondAttendance !== firstAttendance) {
        return secondAttendance - firstAttendance;
      }

      return workerName(first).localeCompare(
        workerName(second)
      );
    });

  const best = ranked[0];
  const alternatives = ranked.slice(1, 4);

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 16,
        border: "1px solid #bfdbfe",
        background: "#eff6ff",
        display: "grid",
        gap: 10,
        marginBottom: 12,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 950,
          color: "#2563eb",
          textTransform: "uppercase",
        }}
      >
        Recommended Worker
      </div>

      <div style={{ fontSize: 12 }}>Weekly availability influences recommendation order.</div>

      {alreadyAssigned ? (
        <div
          style={{
            fontWeight: 900,
            color: "#166534",
          }}
        >
          ✓ This shift already has an assigned worker.
        </div>
      ) : best ? (
        <>
          <div
            style={{
              fontSize: 22,
              fontWeight: 950,
              color: "#0f172a",
              overflowWrap: "anywhere",
            }}
          >
            {workerName(best)}
          </div>
          <WeeklyAvailabilityBadge worker={best} shift={selectedShift} facility={facility} />

          <button
            type="button"
            onClick={() => onAssign?.(best)}
            style={{
              width: "100%",
              border: "none",
              background: "#2563eb",
              color: "#ffffff",
              borderRadius: 12,
              padding: "11px 14px",
              fontWeight: 950,
              cursor: "pointer",
              overflowWrap: "anywhere",
            }}
          >
            Assign {workerName(best)}
          </button>

          <details
            style={{
              borderRadius: 12,
              border: "1px solid #bfdbfe",
              background: "#ffffff",
              overflow: "hidden",
            }}
          >
            <summary
              style={{
                padding: "10px 12px",
                color: "#1d4ed8",
                fontSize: 12,
                fontWeight: 900,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              View recommendation details
            </summary>

            <div
              style={{
                display: "grid",
                gap: 10,
                padding: "2px 12px 12px",
              }}
            >
              <div
                style={{
                  color: "#15803d",
                  fontSize: 13,
                  fontWeight: 950,
                }}
              >
                ⭐ {best._matchScore} Match
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 5,
                  color: "#334155",
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1.4,
                }}
              >
                {best._matchReasons.length ? (
                  best._matchReasons.map(
                    (reason, index) => (
                      <div
                        key={`${reason}-${index}`}
                      >
                        {reason}
                      </div>
                    )
                  )
                ) : (
                  <div>
                    ✓ Best eligible worker based on
                    the combined ranking metrics
                  </div>
                )}
              </div>
            </div>
          </details>

          {alternatives.length > 0 ? (
            <details
              style={{
                borderRadius: 12,
                border: "1px solid #bfdbfe",
                background: "#ffffff",
                overflow: "hidden",
              }}
            >
              <summary
                style={{
                  padding: "10px 12px",
                  color: "#475569",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                Alternative workers (
                {alternatives.length})
              </summary>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                  padding: "2px 12px 12px",
                }}
              >
                {alternatives.map((worker) => (
                  <button
                    type="button"
                    key={
                      worker.uid ||
                      worker.id ||
                      worker.email
                    }
                    onClick={() =>
                      onAssign?.(worker)
                    }
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems: "center",
                      gap: 8,
                      padding: "9px 10px",
                      borderRadius: 9,
                      border:
                        "1px solid #dbeafe",
                      background: "#f8fafc",
                      color: "#0f172a",
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        overflowWrap: "anywhere",
                      }}
                    >
                      {workerName(worker)}
                      <WeeklyAvailabilityBadge worker={worker} shift={selectedShift} facility={facility} />
                    </span>

                    <span
                      style={{
                        color: "#15803d",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ⭐ {worker._matchScore}
                    </span>
                  </button>
                ))}
              </div>
            </details>
          ) : null}
        </>
      ) : (
        <div
          style={{
            fontWeight: 900,
            color: "#92400e",
          }}
        >
          No ready, qualified, non-overlapping workers
          are available for this shift.
        </div>
      )}
    </div>
  );
}